import React, { useEffect, useState } from "react";
import { Bell, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { isNativePlatform } from "../lib/platform";
import { initPushNotifications } from "../lib/nativePush";

/**
 * Push Notifications card — visible only inside the Android app.
 *
 * Lets the user:
 *  • see registered devices count
 *  • re-trigger permission + FCM registration manually
 *  • fire a test push to their own devices
 *
 * The "Register / Re-register" button is critical — if the OS permission
 * popup was dismissed or denied silently at first launch, the user can
 * always retry from here without uninstalling the APK.
 */
export default function PushNotificationsCard() {
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);

  const native = isNativePlatform();

  const refresh = async () => {
    try {
      const { data } = await api.get("/devices/mine");
      setDevices(data.rows || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load devices");
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleRegister = async () => {
    if (!native) {
      toast.info("Yeh sirf Android app ke andar kaam karta hai");
      return;
    }
    setBusy(true);
    try {
      // initPushNotifications now fires success/error toasts itself via the
      // `registration` / `registrationError` listeners (avoids false "denied"
      // toasts caused by stale Capacitor checkPermissions on Android 16).
      await initPushNotifications({});
      toast.info("Permission request bheji gayi — 5 second mein device list refresh hogi.", { duration: 5000 });
      setTimeout(refresh, 5000);
    } catch (e) {
      toast.error(e?.message || "Push registration fail");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/devices/test-push", {
        title: "HisaabPro Test 🎉",
        body: "Push notifications kaam kar rahi hain!",
        path: "/",
      });
      if (data.sent > 0) {
        toast.success(`Push bheji gayi (${data.sent}/${data.sent + data.failed}). Apna phone check karein!`);
      } else if (data.reason === "fcm_not_configured") {
        toast.error("Backend FCM configure nahi hai — admin se contact karein");
      } else if (devices.length === 0) {
        toast.error("Koi device registered nahi — pehle Register button dabayein");
      } else {
        toast.error(`Send fail (${data.failed}). Tokens stale ho sakte hain — Re-register karein.`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Test push fail");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3" data-testid="settings-push-card">
      <div className="flex items-start gap-2">
        <Bell className="w-5 h-5 text-emerald-600 mt-0.5" />
        <div className="flex-1">
          <div className="font-display font-bold text-lg">Push Notifications</div>
          <p className="text-xs text-slate-500">
            Renewal, recovery aur payment due ki alerts seedhi aap ke phone par.
          </p>
        </div>
      </div>

      {/* Status pill */}
      <div className="flex items-center gap-2 text-sm">
        {devices.length > 0 ? (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="font-semibold">{devices.length} device registered</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <AlertCircle className="w-3.5 h-3.5" />
            <span className="font-semibold">Koi device registered nahi</span>
          </div>
        )}
        {!native && (
          <span className="text-xs text-slate-400">(Android app ke andar zaroor open karein)</span>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-600">{error}</div>
      )}

      {/* Buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={handleRegister}
          disabled={busy || !native}
          className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="push-register-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
          {devices.length > 0 ? "Re-register" : "Enable Notifications"}
        </button>
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || devices.length === 0}
          className="px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="push-test-btn"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
          Test Push Bhejein
        </button>
      </div>

      {/* Registered devices list */}
      {devices.length > 0 && (
        <div className="pt-2 space-y-1.5 text-xs">
          <div className="text-slate-400 uppercase tracking-wider font-semibold">Registered devices</div>
          {devices.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-50 rounded">
              <div className="font-mono text-[10px] text-slate-500">{d.device_name || "Android"} · {d.platform}</div>
              <div className="text-[10px] text-slate-400">{(d.last_seen || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
